/**
 * CLICK THE THING, CHANGE THE THING.
 *
 * No edit buttons, no edit mode, no form that opens somewhere else with a
 * copy of the record in it. A copy of a record is a second version of the
 * truth for as long as the form is open, and the last system had several.
 *
 * Enter or clicking away saves. Escape puts it back. That is the whole
 * interaction, and it is the same on every field so nobody has to learn it
 * twice.
 *
 * WHAT IT DOES ABOUT FAILURE. A save that quietly does not happen is worse
 * than one that visibly refuses — you walk away believing the address is
 * fixed. So the field shows what it is doing: saving, saved, or the reason
 * it would not, and on a refusal it puts the old value back rather than
 * leaving the new one on screen looking accepted.
 */

import { esc } from "./dom.ts";

export type EditableSpec = {
  /** Where it lives, for the update. */
  table: string;
  id: string;
  column: string;
  /** What is there now. */
  value: string | null;
  /** Shown when there is nothing. */
  placeholder?: string;
  /** false renders plain text with a reason on hover. */
  editable: boolean;
  reason?: string;
  multiline?: boolean;
  /**
   * OPTIONAL: turn this field into a lookup.
   *
   * Given what has been typed so far, hand back the lines worth offering.
   * The address field uses it (see ui/lookup.ts); nothing else has to know
   * it exists, which is why it is optional rather than a second control.
   *
   * It must never throw for the person typing — an empty list IS the
   * answer when the lookup is down, and the field stays an ordinary text
   * box underneath. Autofill that breaks typing is worse than no autofill.
   */
  suggest?: (typed: string) => Promise<string[]>;
  /** Saves it. Returns nothing, throws with a sentence a person can read. */
  save: (next: string) => Promise<void>;
};

let seq = 0;

/**
 * EVERY EDITABLE FIELD ON THE PAGE, BY ID.
 *
 * Not "pending until bound" — held for as long as the field is on screen.
 * The previous version bound a listener per element and then cleared this
 * map, which made the whole thing depend on bindEditables() being called
 * after the markup reached the DOM and never twice. Anthony could not edit
 * the shop's labor rate or anything else on that page, and I could not
 * reproduce it from outside a login.
 *
 * So the ordering problem is removed rather than guessed at: ONE listener
 * on the document, added once, which finds the field a click landed in and
 * looks its spec up here. There is no longer a moment when the markup
 * exists and the behavior does not, and calling bindEditables() twice, or
 * not at all, changes nothing.
 */
const fields = new Map<string, EditableSpec>();

/** Markup for one field. Call `bindEditables()` once the page is in. */
export function editable(spec: EditableSpec): string {
  const id = `ed${++seq}`;
  fields.set(id, spec);
  // A screen redrawn a hundred times should not carry a hundred dead
  // specs. Ids only ever go up, so the oldest are the gone ones.
  if (fields.size > 400) {
    for (const k of [...fields.keys()].slice(0, 200)) fields.delete(k);
  }
  const shown = (spec.value ?? "").trim();
  if (!spec.editable) {
    return `<span class="ed locked" title="${esc(spec.reason ?? "")}">${
      shown ? esc(shown) : `<i>${esc(spec.placeholder ?? "—")}</i>`}</span>`;
  }
  return `<span class="ed" id="${id}" tabindex="0" role="button" title="Click to change">${
    shown ? esc(shown) : `<i>${esc(spec.placeholder ?? "Add")}</i>`}</span>`;
}

let listening = false;

/**
 * Kept as a named call because every page invokes it and the name says
 * what the page wants. It now only makes sure the one listener exists.
 */
export function bindEditables(): void {
  if (listening) return;
  listening = true;

  const startFrom = (target: EventTarget | null): boolean => {
    const el = (target as HTMLElement | null)?.closest?.(".ed") as HTMLElement | null;
    if (!el || !el.id) return false;
    const spec = fields.get(el.id);
    if (!spec || !spec.editable) return false;
    open(el, spec);
    return true;
  };

  document.addEventListener("click", (e) => { startFrom(e.target); });
  document.addEventListener("keydown", (e) => {
    if ((e as KeyboardEvent).key !== "Enter") return;
    const el = document.activeElement;
    if (el && (el as HTMLElement).classList?.contains("ed") && startFrom(el)) e.preventDefault();
  });
}

function open(el: HTMLElement, spec: EditableSpec): void {
  if (el.dataset["editing"] === "1") return;
  el.dataset["editing"] = "1";
  const before = spec.value ?? "";

  const input = document.createElement(spec.multiline ? "textarea" : "input");
  input.className = "ed-input";
  input.value = before;
  if (spec.multiline) (input as HTMLTextAreaElement).rows = 3;

  el.replaceChildren(input);
  input.focus();
  if (!spec.multiline) (input as HTMLInputElement).select();

  let closed = false;

  /**
   * THE LOOKUP LIST, WHEN THE FIELD HAS ONE.
   *
   * ── THE TRAP THIS IS BUILT AROUND ───────────────────────────────
   *
   * Committing on blur and picking with a click are natural enemies:
   * pressing the mouse on a suggestion blurs the input, which saves
   * whatever was half typed, which tears the list off the screen before
   * the click can land on it. You pick an address and get the fragment.
   *
   * So the list swallows `mousedown` and never lets focus leave. The
   * click then arrives with the input still open and still holding the
   * spec, and `pick()` is the only thing that ever writes a suggestion.
   */
  let box: HTMLDivElement | null = null;
  let options: string[] = [];
  let cursor = -1;
  let timer: ReturnType<typeof setTimeout> | undefined;
  let round = 0;

  /* `ed-lookup` is what makes `.ed` a positioning context for the list.
     It is a class rather than a `:has()` rule because the build targets
     safari15, where `:has()` does not fail loudly — it just never
     matches, and the dropdown lands somewhere else on the page. */
  const shut = (): void => {
    box?.remove(); box = null; options = []; cursor = -1;
    el.classList.remove("ed-lookup");
  };

  const draw = (): void => {
    if (!options.length) return shut();
    if (!box) {
      el.classList.add("ed-lookup");
      box = document.createElement("div");
      box.className = "ed-suggest";
      // Focus must not leave the input, or blur commits the fragment.
      box.addEventListener("mousedown", (e) => e.preventDefault());
      box.addEventListener("click", (e) => {
        const row = (e.target as HTMLElement).closest<HTMLElement>("[data-i]");
        if (row) pick(Number(row.dataset["i"]));
      });
      el.appendChild(box);
    }
    box.innerHTML = options.map((line, i) =>
      `<div class="ed-sug ${i === cursor ? "on" : ""}" data-i="${i}">${esc(line)}</div>`).join("");
  };

  const pick = (i: number): void => {
    const line = options[i];
    if (line === undefined) return;
    input.value = line;
    shut();
    input.blur();   // blur commits, and now it commits the whole address
  };

  const ask = (): void => {
    if (!spec.suggest || spec.multiline) return;
    clearTimeout(timer);
    const typed = input.value.trim();
    if (typed.length < 3) return shut();
    // A quarter second after the last keystroke. Fast enough to feel like
    // the field is keeping up, slow enough that a typed address is one
    // lookup rather than thirty.
    timer = setTimeout(() => {
      const mine = ++round;
      void spec.suggest!(typed).then((lines) => {
        // A slower earlier answer must not overwrite a newer one.
        if (closed || mine !== round) return;
        options = lines.slice(0, 6);
        cursor = -1;
        draw();
      }).catch(() => shut());
    }, 250);
  };

  input.addEventListener("input", ask);
  const paint = (text: string, cls = "") => {
    el.className = `ed ${cls}`.trim();
    el.dataset["editing"] = "0";
    el.innerHTML = text.trim() ? esc(text) : `<i>${esc(spec.placeholder ?? "Add")}</i>`;
    // Nothing to re-arm: the document listener is still there and the
    // spec is still in the map. The old version added a one-shot listener
    // here, which meant a field could be corrected exactly twice.
    spec.value = text;
  };

  const cancel = () => { if (!closed) { closed = true; paint(before); } };

  const commit = async () => {
    if (closed) return;
    closed = true;
    const next = input.value.trim();
    if (next === before.trim()) { paint(before); return; }

    el.className = "ed saving";
    el.textContent = next || "—";
    try {
      await spec.save(next);
      spec.value = next;
      paint(next, "saved");
      setTimeout(() => { el.className = "ed"; }, 1200);
    } catch (e) {
      // The old value goes back. Leaving the new one on screen after a
      // refusal is how somebody walks away thinking it saved.
      paint(before, "failed");
      el.title = e instanceof Error ? e.message : String(e);
      setTimeout(() => { el.className = "ed"; el.title = "Click to change"; }, 3200);
    }
  };

  input.addEventListener("blur", () => { clearTimeout(timer); shut(); void commit(); });
  input.addEventListener("keydown", (e) => {
    const k = (e as KeyboardEvent).key;

    /* THE LIST GETS THE KEY FIRST, when it is up. Escape closes the list
       rather than throwing the edit away — one press to change your mind
       about the suggestions, a second to change your mind about the edit,
       which is what every other lookup field in the world does. */
    if (options.length) {
      if (k === "ArrowDown" || k === "ArrowUp") {
        e.preventDefault();
        cursor = k === "ArrowDown"
          ? (cursor + 1) % options.length
          : (cursor <= 0 ? options.length - 1 : cursor - 1);
        draw();
        return;
      }
      if (k === "Enter" && cursor >= 0) { e.preventDefault(); pick(cursor); return; }
      if (k === "Escape") { e.preventDefault(); shut(); return; }
    }

    if (k === "Escape") { e.preventDefault(); input.blur(); cancel(); }
    // Enter saves a single line. In a textarea it is a new line, so those
    // save on blur or with the modifier.
    if (k === "Enter" && (!spec.multiline || (e as KeyboardEvent).metaKey || (e as KeyboardEvent).ctrlKey)) {
      e.preventDefault(); input.blur();
    }
  });
}
